import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, ExpandMore as ExpandMoreIcon,
  PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError, extractInventoryErrorDetail } from '../../../../services/inventoryItemService';
import inventoryCategoryService from '../../../../services/inventoryCategoryService';
import type { WineSearchResult, Wine } from '../../../../services/wineService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import ScanToWineDialog from '../../../../components/scan/ScanToWineDialog';
import type { WineSearchSeed } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import SimpleWineCreateDialog from '../../../../components/venue/SimpleWineCreateDialog';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import type { Menu, MenuVisibility } from '../../../../types/menu';
import type { InventoryCategory, InventoryItem, InventorySubcategory, InventoryItemType, SizeUnit } from '../../../../types/venue';
import { humanizeItemType } from '../../../../utils/inventoryItemDisplay';

const SIZE_UNITS: SizeUnit[] = ['ml', 'cl', 'l', 'g', 'kg', 'unit'];

const InventoryItemForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { itemId } = useParams<{ itemId?: string }>();
  const isEdit = Boolean(itemId);

  const backPath = isEdit
    ? `/admin/venues/${venueId}/inventory/${itemId}`
    : `/admin/venues/${venueId}/inventory`;

  // ── Edit mode: load existing item ─────────────────────────────────────────────
  const [initLoading, setInitLoading] = useState(isEdit);
  const [initError, setInitError] = useState<string | null>(null);
  const [loadedItem, setLoadedItem] = useState<InventoryItem | null>(null);

  // ── Create mode: item type toggle ─────────────────────────────────────────────
  const [itemType, setItemType] = useState<InventoryItemType>('PURCHASED');
  /**
   * ITEM-TYPE-CORRECTION.1 — correcting an item entered under the wrong type.
   *
   * Clients add a bottle through this generic form instead of the wine one. The
   * API has always allowed the correction and has always left the shortId
   * alone, so the QR labels already on the shelves survive it — but the type
   * control rendered on CREATE only, so nobody could reach it.
   *
   * Opt-in rather than always-on: changing an item's type is a correction, not
   * an edit, and a control sitting open on every visit invites the mistake it
   * exists to undo.
   */
  const [correctingType, setCorrectingType] = useState(false);

  // ── User-definable category / subcategory ─────────────────────────────────────
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [categoryId, setCategoryId] = useState<string>('');
  const [subcategories, setSubcategories] = useState<InventorySubcategory[]>([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [subcategoryId, setSubcategoryId] = useState<string>('');

  // ── Create mode: non-wine — display name ─────────────────────────────────────
  const [displayName, setDisplayName] = useState('');

  // ── Create mode: WINE — wine search ──────────────────────────────────────────
  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  // Scanning is an ENTRY METHOD into this very flow: it replaces typing a
  // search, hands back the wine, and the user finishes quantity, size and
  // price exactly as before.
  const [scanOpen, setScanOpen] = useState(false);
  const [createWineOpen, setCreateWineOpen] = useState(false);
  const [createWineQuery, setCreateWineQuery] = useState('');
  // SIMPLE-WINE.1 — the simple dialog is what "add a wine we don't have" now
  // opens. The structured one is still here, one tap behind it, unchanged.
  const [simpleWineOpen, setSimpleWineOpen] = useState(false);
  // Bumped when the create modal refuses a duplicate and hands the existing
  // wine name back — re-runs the picker search on that name.
  const [wineSearchSeed, setWineSearchSeed] = useState<WineSearchSeed>({ query: '', nonce: 0 });

  // ── Size (create mode) ────────────────────────────────────────────────────────
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState<SizeUnit>('ml');

  // ── Config (create + edit) ────────────────────────────────────────────────────
  const [parLevel, setParLevel] = useState('');
  const [reorderPoint, setReorderPoint] = useState('');
  const [binLocation, setBinLocation] = useState('');
  const [lotTracked, setLotTracked] = useState(false);

  // ── Default selling price (POS) — edited in MAJOR units, stored in MINOR units ─
  const [salePrice, setSalePrice] = useState('');

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ITEM-MASTER — set when the name is already defined by a SISTER venue. The
  // item master is company-scoped, so this is not a duplicate to rename around:
  // it is the same product, and the right move is to stock the existing master.
  // Holding its id here turns a dead-end 409 into a one-click action.
  const [existingCompanyItemId, setExistingCompanyItemId] = useState<string | null>(null);
  const [stocking, setStocking] = useState(false);

  // ── Validation ────────────────────────────────────────────────────────────────
  const [touched, setTouched] = useState(false);

  const genericNameError = touched && !isEdit && itemType !== 'WINE' && !displayName.trim();
  const wineError = touched && !isEdit && itemType === 'WINE' && !selectedWine;
  // HOTFIX-UI-3 (fix 5) — WINE items require a size (backend enforces
  // WINE_SIZE_REQUIRED; size is immutable identity, so it must exist up front).
  const wineSizeError = touched && !isEdit && itemType === 'WINE' && sizeValue === '';

  const parLevelNum = parLevel !== '' ? Number(parLevel) : undefined;
  const reorderPointNum = reorderPoint !== '' ? Number(reorderPoint) : undefined;
  const sizeValueNum = sizeValue !== '' ? Number(sizeValue) : undefined;
  const reorderExceedsPar =
    parLevelNum !== undefined && reorderPointNum !== undefined && reorderPointNum > parLevelNum;

  // Blank salePrice is allowed; if present it must be a non-negative number.
  const salePriceInvalid =
    salePrice.trim() !== '' && (isNaN(parseFloat(salePrice)) || parseFloat(salePrice) < 0);

  // ── Post-create: add to menu dialog (wine-linked items only) ──────────────────
  // INVENTORY-MENU-PLACEMENT.1 — placement writes go to the new Menu/MenuItem
  // model. Inventory workflows no longer create legacy WineListItem records
  // (legacy wine lists remain readable elsewhere; no data is migrated here).
  const [postCreate, setPostCreate] = useState<{
    itemId: string;
    wineId: string;
    sizeValue?: number;
    /** wineDisplay payload for the WINE menu item, captured at create time. */
    wineDisplay: {
      name: string;
      displayVintage: string;
      producerName: string;
      regionName: string;
      color: string;
    };
  } | null>(null);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [menuItemVisibility, setMenuItemVisibility] = useState<MenuVisibility>('LIVE');
  const [addingToMenu, setAddingToMenu] = useState(false);
  const [addToMenuError, setAddToMenuError] = useState('');

  const navigateToCreated = (createdItemId: string) =>
    navigate(`/admin/venues/${venueId}/inventory/${createdItemId}`);

  const handleSkipMenu = () => {
    if (postCreate) navigateToCreated(postCreate.itemId);
  };

  const handleAddToMenu = async () => {
    if (!postCreate || !selectedMenuId) return;
    setAddingToMenu(true);
    setAddToMenuError('');
    try {
      await menuItemService.createItem(venueId, selectedMenuId, {
        kind: 'WINE',
        displaySnapshot: { name: postCreate.wineDisplay.name },
        sectionId: null,
        position: 0,
        visibility: menuItemVisibility,
        source: 'MANUAL',
        needsReview: false,
        inventoryItemId: postCreate.itemId,
        ...(defaultCurrency && { currency: defaultCurrency }),
        wineDetails: {
          wineId: postCreate.wineId,
          wineDisplay: postCreate.wineDisplay,
          ...(postCreate.sizeValue != null && { bottleSize: postCreate.sizeValue }),
        },
      } as Parameters<typeof menuItemService.createItem>[2]);
      navigateToCreated(postCreate.itemId);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? '';
      if (status === 409 || msg.toLowerCase().includes('exists') || msg.toLowerCase().includes('duplicate')) {
        setAddToMenuError('This wine is already on the selected menu.');
      } else {
        setAddToMenuError('Failed to add to menu. Please try again.');
      }
    } finally {
      setAddingToMenu(false);
    }
  };

  // ── Create new wine: convert Wine → WineSearchResult and select ──────────────
  const handleWineCreated = (wine: Wine) => {
    setSelectedWine({
      _id: wine._id,
      name: wine.name,
      displayVintage: wine.displayVintage,
      vintageType: wine.vintageType,
      wineDisplay: {
        producerName: wine.wineName.producer?.name,
        regionName: wine.wineName.region?.name,
        color: wine.wineName.color,
      },
    });
    setCreateWineOpen(false);
    setSimpleWineOpen(false);
  };

  // ── Load item for edit mode ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !itemId || !venueId) return;
    setInitLoading(true);
    inventoryItemService.getItem(venueId, itemId)
      .then(item => {
        setLoadedItem(item);
        if (item.parLevel != null) setParLevel(String(item.parLevel));
        if (item.reorderPoint != null) setReorderPoint(String(item.reorderPoint));
        if (item.binLocation) setBinLocation(item.binLocation);
        setLotTracked(item.lotTracked ?? false);
        setCategoryId(item.categoryId ?? '');
        setSubcategoryId(item.subcategoryId ?? '');
        // salePrice is stored in minor units; edit in major units.
        if (item.salePrice != null) setSalePrice((item.salePrice / 100).toFixed(2));
        setInitError(null);
      })
      .catch(() => setInitError('Failed to load inventory item.'))
      .finally(() => setInitLoading(false));
  }, [isEdit, itemId, venueId]);

  // ── Load active categories ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    inventoryCategoryService.getCategories(venueId, {})
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [venueId]);

  // ── Load subcategories whenever a category is selected ─────────────────────────
  useEffect(() => {
    if (!venueId || !categoryId) {
      setSubcategories([]);
      return;
    }
    setSubcategoriesLoading(true);
    inventoryCategoryService.getSubcategories(venueId, categoryId, {})
      .then(setSubcategories)
      .catch(() => setSubcategories([]))
      .finally(() => setSubcategoriesLoading(false));
  }, [venueId, categoryId]);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTouched(true);
    if (isEdit && correctingType && itemType === 'WINE') {
      if (!selectedWine) return;
      // A wine is identified by its bottle size, and one that never had a size
      // has to gain one here — it cannot be added later.
      if (loadedItem?.sizeValue == null && sizeValue === '') return;
    }
    if (!isEdit) {
      if (itemType !== 'WINE' && !displayName.trim()) return;
      if (itemType === 'WINE' && !selectedWine) return;
      if (itemType === 'WINE' && sizeValue === '') return; // WINE requires a size
    }
    if (reorderExceedsPar) return;
    if (salePriceInvalid) return;

    // Convert MAJOR-unit input to MINOR units (cents) for the backend.
    const salePriceMinor =
      salePrice.trim() !== '' ? Math.round(parseFloat(salePrice) * 100) : undefined;
    // Create: include only when set. Update: blank clears via null.
    const createSalePriceFields =
      salePriceMinor !== undefined
        ? { salePrice: salePriceMinor, ...(defaultCurrency && { salePriceCurrency: defaultCurrency }) }
        : {};

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && itemId) {
        const correction = correctingType && itemType !== loadedItem?.type
          ? {
              type: itemType,
              ...(itemType === 'WINE' ? { wineId: selectedWine!._id } : {}),
              // Only when the item has none: the API refuses any CHANGE to an
              // existing size, and sending one back unchanged would trip that.
              ...(itemType === 'WINE' && loadedItem?.sizeValue == null && sizeValue !== ''
                ? { sizeValue: Number(sizeValue), sizeUnit }
                : {}),
            }
          : {};

        await inventoryItemService.updateItem(venueId, itemId, {
          ...correction,
          ...(parLevel !== '' ? { parLevel: Number(parLevel) } : {}),
          ...(reorderPoint !== '' ? { reorderPoint: Number(reorderPoint) } : {}),
          binLocation: binLocation.trim() || null,
          categoryId: categoryId || null,
          subcategoryId: categoryId ? (subcategoryId || null) : null,
          lotTracked,
          // Blank clears the POS price; otherwise store minor units + venue currency.
          salePrice: salePriceMinor ?? null,
          salePriceCurrency: salePriceMinor !== undefined ? (defaultCurrency ?? null) : null,
        });
        navigate(backPath);
      } else {
        const binLocationTrimmed = binLocation.trim() || undefined;
        const categoryFields = categoryId
          ? { categoryId, ...(subcategoryId && { subcategoryId }) }
          : {};
        const payload =
          itemType !== 'WINE'
            ? {
                type: itemType as 'PURCHASED' | 'MANUFACTURED' | 'RECIPE',
                displayName: displayName.trim(),
                ...categoryFields,
                ...(sizeValueNum !== undefined && { sizeValue: sizeValueNum, sizeUnit }),
                ...(parLevelNum !== undefined && { parLevel: parLevelNum }),
                ...(reorderPointNum !== undefined && { reorderPoint: reorderPointNum }),
                ...(binLocationTrimmed !== undefined && { binLocation: binLocationTrimmed }),
                lotTracked,
                ...createSalePriceFields,
              }
            : {
                type: 'WINE' as const,
                wineId: selectedWine!._id,
                ...categoryFields,
                ...(sizeValueNum !== undefined && { sizeValue: sizeValueNum, sizeUnit }),
                ...(parLevelNum !== undefined && { parLevel: parLevelNum }),
                ...(reorderPointNum !== undefined && { reorderPoint: reorderPointNum }),
                ...(binLocationTrimmed !== undefined && { binLocation: binLocationTrimmed }),
                lotTracked,
                ...createSalePriceFields,
              };

        const created = await inventoryItemService.createItem(venueId, payload);

        // RECIPE-ITEM-CREATE.1 — a recipe item is not finished at create: it
        // depletes nothing until its ingredients exist. Land on the item page,
        // where the Recipe section is, rather than the list.
        if (created.type === 'RECIPE') {
          navigateToCreated(created._id);
          return;
        }

        // Only offer the menu prompt when the created item is linked to a wine
        if (created.wineId) {
          setMenusLoading(true);
          try {
            const result = await menuService.getMenus(venueId, 1, 100, undefined, 'all');
            const activeMenus = result.data.filter(m => m.status !== 'ARCHIVED');
            setMenus(activeMenus);
            if (activeMenus.length > 0) setSelectedMenuId(activeMenus[0]._id);
          } catch {
            setMenus([]);
          } finally {
            setMenusLoading(false);
          }
          setPostCreate({
            itemId: created._id,
            wineId: created.wineId,
            sizeValue: created.sizeValue,
            // Backend requires all 5 wineDisplay fields as strings; empty
            // string is accepted for unknown values.
            wineDisplay: {
              name: selectedWine?.name ?? created.displayName,
              displayVintage: selectedWine?.displayVintage ?? '',
              producerName: selectedWine?.wineDisplay?.producerName ?? '',
              regionName: selectedWine?.wineDisplay?.regionName ?? '',
              color: selectedWine?.wineDisplay?.color ?? '',
            },
          });
        } else {
          navigateToCreated(created._id);
        }
      }
    } catch (err) {
      const detail = extractInventoryErrorDetail(err);
      setError(detail.message);
      setExistingCompanyItemId(
        detail.code === 'INVENTORY_ITEM_EXISTS_IN_COMPANY' ? detail.inventoryItemId : null,
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Stock the master a sister venue already defines, rather than creating a
   * second one. Carries across the price typed into the form, since that is
   * per-venue and is what the user was in the middle of setting.
   */
  const handleStockExisting = async () => {
    if (!existingCompanyItemId || !venueId) return;
    setStocking(true);
    try {
      const priceMinor = salePrice.trim() !== '' ? Math.round(Number(salePrice) * 100) : undefined;
      await inventoryItemService.setVenueSettings(venueId, existingCompanyItemId, {
        active: true,
        ...(priceMinor !== undefined && Number.isFinite(priceMinor) ? { salePrice: priceMinor } : {}),
      });
      navigate(`/admin/venues/${venueId}/inventory/${existingCompanyItemId}`);
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setStocking(false);
    }
  };

  // ── Loading state (edit init) ─────────────────────────────────────────────────
  if (initLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: theme.palette.text.secondary }}>
          Back
        </Button>
        <Alert severity="error">{initError}</Alert>
      </Box>
    );
  }

  // ── Category / subcategory selectors (shared by create + edit) ────────────────
  // An item may hold a category/subcategory that has since been deactivated and
  // so is absent from the active picker lists. Surface it as a disabled
  // "(inactive)" option so the Select shows a label instead of rendering blank.
  const categoryMissing = !!categoryId && !categories.some(c => c._id === categoryId);
  const subcategoryMissing = !!subcategoryId && !subcategories.some(s => s._id === subcategoryId);
  const categorySelectors = (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={categoryId}
          label="Category"
          onChange={e => { setCategoryId(e.target.value); setSubcategoryId(''); }}
        >
          <MenuItem value="">— None —</MenuItem>
          {categories.map(c => (
            <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
          ))}
          {categoryMissing && (
            <MenuItem value={categoryId} disabled>
              {(loadedItem?.categoryName ?? 'Category')} (inactive)
            </MenuItem>
          )}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 220 }} disabled={!categoryId || subcategoriesLoading}>
        <InputLabel>Subcategory</InputLabel>
        <Select
          value={subcategoryId}
          label="Subcategory"
          onChange={e => setSubcategoryId(e.target.value)}
        >
          <MenuItem value="">— None —</MenuItem>
          {subcategories.map(s => (
            <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
          ))}
          {subcategoryMissing && (
            <MenuItem value={subcategoryId} disabled>
              {(loadedItem?.subcategoryName ?? 'Subcategory')} (inactive)
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 680 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            {isEdit ? 'Item Detail' : 'Inventory'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>
            {isEdit ? 'Edit Item Configuration' : 'Add Inventory Item'}
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && !existingCompanyItemId && (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          )}

          {/* Not an error the user can only read — an action they can take. */}
          {existingCompanyItemId && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={stocking}
                  onClick={handleStockExisting}
                >
                  {stocking ? 'Adding…' : 'Stock it here'}
                </Button>
              }
              onClose={() => { setExistingCompanyItemId(null); setError(null); }}
            >
              Another venue in your company already has this product. Add it to this
              venue instead of creating a second copy — stock and pricing stay separate.
            </Alert>
          )}

          {/* ── ITEM-TYPE-CORRECTION.1 — the type was entered wrongly ─────────── */}
          {/* BASIC is excluded: the API fails closed in BOTH directions
              (BASIC-ITEM.C2), so every transition this box could offer would
              422. The rest of edit mode already renders BASIC read-only. */}
          {isEdit && loadedItem && loadedItem.type !== 'BASIC' && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}` }}>
              {!correctingType ? (
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Item type: {loadedItem.type === 'WINE' ? 'Wine' : loadedItem.type.charAt(0) + loadedItem.type.slice(1).toLowerCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Entered under the wrong type? Correcting it keeps the item's
                      short ID, so printed QR labels stay valid, and keeps its stock.
                    </Typography>
                  </Box>
                  <Button
                    size="small" variant="outlined"
                    onClick={() => { setCorrectingType(true); setItemType(loadedItem.type); }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Correct type
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={600}>Correct item type</Typography>
                  <ToggleButtonGroup
                    value={itemType}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) setItemType(val); }}
                  >
                    {/* Only what the API permits from here. BASIC converts to
                        nothing in either direction, and RECIPE only to and from
                        PURCHASED — offering more would be a button that 422s. */}
                    <ToggleButton value="PURCHASED" sx={{ textTransform: 'none', fontWeight: 600 }}>Purchased</ToggleButton>
                    <ToggleButton value="WINE" sx={{ textTransform: 'none', fontWeight: 600 }}>Wine</ToggleButton>
                    <ToggleButton value="MANUFACTURED" sx={{ textTransform: 'none', fontWeight: 600 }}>Manufactured</ToggleButton>
                    {(loadedItem.type === 'PURCHASED' || loadedItem.type === 'RECIPE') && (
                      <ToggleButton value="RECIPE" sx={{ textTransform: 'none', fontWeight: 600 }}>Recipe</ToggleButton>
                    )}
                  </ToggleButtonGroup>

                  {itemType === 'WINE' && loadedItem.type !== 'WINE' && (
                    <>
                      {/* The same picker the create path uses — one way to
                          choose a wine, so the two cannot drift. */}
                      <WineSearchBox
                        value={selectedWine}
                        onChange={wine => setSelectedWine(wine)}
                        onCreateNew={query => { setCreateWineQuery(query); setSimpleWineOpen(true); }}
                        error={touched && !selectedWine}
                        helperText={touched && !selectedWine ? 'Select the wine this item is' : undefined}
                        venueId={venueId}
                        seed={wineSearchSeed}
                      />
                      {/* Only when the item never had one. The API refuses any
                          change to a size that is already set, because stock is
                          held in units tied to it. */}
                      {loadedItem.sizeValue == null && (
                        <Stack direction="row" spacing={1.5}>
                          <TextField
                            size="small" label="Bottle size" value={sizeValue}
                            onChange={(e) => setSizeValue(e.target.value)}
                            error={touched && sizeValue === ''}
                            helperText={touched && sizeValue === '' ? 'A wine is identified by its bottle size.' : ' '}
                            sx={{ maxWidth: 180 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Unit</InputLabel>
                            <Select label="Unit" value={sizeUnit} onChange={(e) => setSizeUnit(e.target.value as typeof sizeUnit)}>
                              <MenuItem value="ml">ml</MenuItem>
                              <MenuItem value="cl">cl</MenuItem>
                              <MenuItem value="L">L</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      )}
                      <Alert severity="info" sx={{ borderRadius: 1 }}>
                        The item will be renamed after the wine. Its short ID,
                        stock and history are untouched.
                      </Alert>
                    </>
                  )}

                  <Box>
                    <Button size="small" onClick={() => { setCorrectingType(false); setItemType(loadedItem.type); setSelectedWine(null); }} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          )}

          {/* ── Create mode ────────────────────────────────────────────────────── */}
          {!isEdit && (
            <>
              {/* Item type toggle */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Item Type</Typography>
                <ToggleButtonGroup
                  value={itemType}
                  exclusive
                  onChange={(_, val) => {
                    if (val) {
                      setItemType(val);
                      setDisplayName('');
                      setSelectedWine(null);
                      // Category is chosen manually — no auto-selection by type.
                      setCategoryId('');
                      setSubcategoryId('');
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="PURCHASED" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Purchased
                  </ToggleButton>
                  <ToggleButton value="WINE" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Wine
                  </ToggleButton>
                  <ToggleButton value="MANUFACTURED" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Manufactured
                  </ToggleButton>
                  {/* RECIPE-ITEM-CREATE.1 — a recipe item was previously only
                      creatable as a side effect of linking a menu format, so a
                      cocktail or a beer pour could not be brought into existence
                      from Inventory at all. It is an inventory concept first:
                      the same pour may appear on several menus, or none. */}
                  <ToggleButton value="RECIPE" sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                    Recipe
                  </ToggleButton>
                </ToggleButtonGroup>
                {itemType === 'RECIPE' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Sold as one thing, depletes several. Save this, then add its
                    ingredients on the item page.
                  </Typography>
                )}
              </Box>

              {/* Non-wine: free-text name */}
              {itemType !== 'WINE' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Name</Typography>
                  <TextField
                    fullWidth
                    label="Display Name"
                    required
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    error={genericNameError}
                    helperText={genericNameError ? 'Enter a name for this item' : 'e.g. "Still Water 500 ml", "House Red"'}
                    inputProps={{ maxLength: 200 }}
                  />
                </Box>
              )}

              {/* WINE: wine search autocomplete */}
              {itemType === 'WINE' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Wine</Typography>
                  <WineSearchBox
                    value={selectedWine}
                    onChange={wine => setSelectedWine(wine)}
                    onCreateNew={query => { setCreateWineQuery(query); setSimpleWineOpen(true); }}
                    error={wineError}
                    helperText={wineError ? 'Select a wine' : undefined}
                    venueId={venueId}
                    seed={wineSearchSeed}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<CameraIcon />}
                    onClick={() => setScanOpen(true)}
                    sx={{ mt: 1, textTransform: 'none', py: 1.25 }}
                  >
                    Scan the label instead
                  </Button>
                  <ScanToWineDialog
                    open={scanOpen}
                    venueId={venueId}
                    onClose={() => setScanOpen(false)}
                    onResolved={(w) => {
                      // Straight back into this flow with the wine chosen.
                      // The staff path always returns a wineId (it resolves
                      // or drafts one); the guard makes that explicit.
                      if (!w.wineId) return;
                      setSelectedWine({
                        _id: w.wineId, name: w.name,
                        displayVintage: w.displayVintage,
                        vintageType: w.vintageType as any,
                      });
                      setScanOpen(false);
                    }}
                  />
                </Box>
              )}

              {/* Category / Subcategory (optional metadata) */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Category <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                </Typography>
                {categorySelectors}
              </Box>

              {/* Size — omitted for RECIPE: the item never holds a balance of
                  its own, so a stock-unit size would be identity-bearing
                  nonsense. Its components carry the sizes that matter. */}
              {itemType !== 'RECIPE' && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Size <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    size="small"
                    label={itemType === 'WINE' ? 'Size (required)' : 'Size'}
                    type="number"
                    value={sizeValue}
                    onChange={e => setSizeValue(e.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                    required={itemType === 'WINE'}
                    error={wineSizeError}
                    helperText={wineSizeError
                      ? 'Size is required for wine items'
                      : 'Becomes part of item identity — cannot be changed later'}
                    sx={{ flex: 1, minWidth: 120 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 90 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={sizeUnit}
                      label="Unit"
                      onChange={e => setSizeUnit(e.target.value as SizeUnit)}
                    >
                      {SIZE_UNITS.map(u => (
                        <MenuItem key={u} value={u}>{u}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              )}
            </>
          )}

          {/* ── Edit mode: read-only identity ──────────────────────────────────── */}
          {isEdit && loadedItem && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                    Item
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.25 }}>
                    {loadedItem.displayName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                      Type
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {humanizeItemType(loadedItem.type)}
                    </Typography>
                  </Box>
                  {loadedItem.sizeValue != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        Size
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {loadedItem.sizeValue} {loadedItem.sizeUnit ?? 'ml'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size is part of item identity. Create a new variant for another size.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Alert severity="info" icon={false}>
                Name, type, and size cannot be changed after creation.
                To change them, deactivate this item and create a new one.
              </Alert>
            </>
          )}

          <Divider />

          {/* Configuration */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Configuration
              {!isEdit && (
                <Typography component="span" variant="caption" color="text.secondary"> (optional)</Typography>
              )}
            </Typography>
            {isEdit && (
              <Box sx={{ mb: 2 }}>
                {categorySelectors}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Par Level"
                type="number"
                value={parLevel}
                onChange={e => setParLevel(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Target stock level"
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                size="small"
                label={(isEdit ? loadedItem?.type : itemType) === 'MANUFACTURED' ? 'Production Point' : 'Reorder Point'}
                type="number"
                value={reorderPoint}
                onChange={e => setReorderPoint(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={reorderExceedsPar}
                helperText={reorderExceedsPar ? 'Must be ≤ par level' : (isEdit ? loadedItem?.type : itemType) === 'MANUFACTURED' ? 'When stock falls below this level, produce more.' : 'When stock falls below this level, reorder.'}
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>
            <TextField
              size="small"
              label="Bin Location"
              value={binLocation}
              onChange={e => setBinLocation(e.target.value)}
              placeholder="e.g. A3, Cellar-2 (optional)"
              helperText="Physical storage position"
              inputProps={{ maxLength: 100 }}
              sx={{ maxWidth: 300 }}
            />
            <TextField
              size="small"
              label="Default Selling Price"
              type="number"
              value={salePrice}
              onChange={e => setSalePrice(e.target.value)}
              error={salePriceInvalid}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={
                defaultCurrency
                  ? { endAdornment: <InputAdornment position="end">{defaultCurrency}</InputAdornment> }
                  : undefined
              }
              helperText={
                salePriceInvalid
                  ? 'Must be a non-negative number.'
                  : 'Used when this item is sold directly through POS.'
              }
              sx={{ maxWidth: 300 }}
            />
          </Box>

          {/* Advanced inventory settings */}
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 48 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Advanced inventory settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={lotTracked}
                    onChange={e => setLotTracked(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Lot / batch tracking</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requires a lot to be specified on all future receipts and transfers for this item.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5, ml: 0 }}
              />
              {lotTracked && (
                <Alert severity="warning" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
                  <Typography variant="caption">
                    Existing stock will remain in the unassigned stock bucket. Future receipts and transfers will require a lot.
                  </Typography>
                </Alert>
              )}
              {isEdit && !lotTracked && (loadedItem?.lotTracked ?? false) && (
                <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
                  <Typography variant="caption">
                    Disabling lot tracking does not remove existing lot records.
                  </Typography>
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Opening stock note (create only) */}
          {!isEdit && (
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.07),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              }}
            >
              <Typography variant="body2" color="info.dark">
                The item starts with a <strong>quantity of 0</strong>. Post a Manual Adjustment
                to set opening stock.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save Changes' : 'Create Item'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <SimpleWineCreateDialog
        open={simpleWineOpen}
        venueId={venueId!}
        initialQuery={createWineQuery}
        onCreated={handleWineCreated}
        onSelectExisting={wine => {
          // The cheapest correction there is: it happens before the mistake.
          setSimpleWineOpen(false);
          setSelectedWine(wine);
        }}
        onEnterFullDetails={query => {
          setSimpleWineOpen(false);
          setCreateWineQuery(query);
          setCreateWineOpen(true);
        }}
        onClose={() => setSimpleWineOpen(false)}
      />

      <CreateWineModal
        open={createWineOpen}
        initialQuery={createWineQuery}
        onCreated={handleWineCreated}
        onSelectExisting={wineName => {
          setCreateWineOpen(false);
          setWineSearchSeed(prev => ({ query: wineName.displayName, nonce: prev.nonce + 1 }));
        }}
        onClose={() => setCreateWineOpen(false)}
      />

      {/* Post-create: add to menu dialog (shown only for wine-linked items).
          Writes a kind=WINE MenuItem — never a legacy WineListItem. */}
      <Dialog
        open={Boolean(postCreate)}
        onClose={handleSkipMenu}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Item created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Would you like to also add this wine to a menu?
          </Typography>

          {addToMenuError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddToMenuError('')}>
              {addToMenuError}
            </Alert>
          )}

          {menusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : menus.length === 0 ? (
            <Alert severity="info" icon={false}>
              No active menus found for this venue. Create a menu first under the
              Menus tab — items are placed on menus now, not legacy wine lists.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Menu</InputLabel>
                <Select
                  value={selectedMenuId}
                  label="Menu"
                  onChange={e => { setSelectedMenuId(e.target.value); setAddToMenuError(''); }}
                >
                  <MenuItem value="">— Select a menu —</MenuItem>
                  {menus.map(m => (
                    <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={menuItemVisibility}
                  label="Visibility"
                  onChange={e => setMenuItemVisibility(e.target.value as MenuVisibility)}
                >
                  <MenuItem value="HIDDEN">Hidden</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleSkipMenu} sx={{ textTransform: 'none' }} disabled={addingToMenu}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToMenu}
            disabled={addingToMenu || !selectedMenuId || menusLoading}
            startIcon={addingToMenu ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {addingToMenu ? 'Adding…' : 'Add to Menu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryItemForm;
